import React, { useEffect, useMemo, useRef, useState } from 'react'
import Select from 'react-select'
import { ajaxPerso, fetchItems } from '../../fnc.js'

const refConst = require('../../constants.js').v

const apiForStore = (storeId, marketplaceId) => {
	if (Number(storeId) === Number(refConst.id_store_finale)) return 'finale'
	if ([refConst.id_store_eio, refConst.id_store_hausfTools, refConst.id_store_brandnameapp].map(Number).includes(Number(storeId)) || Number(marketplaceId) === Number(refConst.id_marketplace_shopify)) return 'shopify'
	if ([refConst.id_store_eBay_EIO, refConst.id_store_eBay_YWE].map(Number).includes(Number(storeId)) || Number(marketplaceId) === Number(refConst.id_marketplace_ebay_eio)) return 'ebay'
	if ([refConst.id_store_amazon_EIO, refConst.id_store_amazon_GOTT].map(Number).includes(Number(storeId)) || Number(marketplaceId) === Number(refConst.id_marketplace_amazon_eio)) return 'amazon'
	if (Number(storeId) === Number(refConst.id_store_walmart_eio) || Number(marketplaceId) === Number(refConst.id_marketplace_walmart)) return 'walmart'
	return null
}

const requestRefresh = (args) => new Promise((resolve) => {
	ajaxPerso(args, (result) => resolve(result || { success: false, error: 'The API returned no result.' }), (xhr, status, error) => {
		resolve({ success: false, error: error || status || 'The refresh request failed.' })
	})
})

const errorText = (result) => {
	const error = result?.errors || result?.error
	if (!error) return null
	if (Array.isArray(error) && error.length === 0) return null
	if (typeof error === 'object' && Object.keys(error).length === 0) return null
	if (typeof error === 'string') return error
	try {
		return JSON.stringify(error)
	} catch (_) {
		return 'An unexpected error was returned.'
	}
}

const normalizeSkuList = (skuList) => Array.from(new Set((skuList || []).map((sku) => String(sku || '').trim()).filter(Boolean)))

const successText = (result) => {
	const details = []
	if (Number.isFinite(Number(result?.skuCountReceived))) details.push(`${result.skuCountReceived} SKU${Number(result.skuCountReceived) === 1 ? '' : 's'} refreshed`)
	if (Array.isArray(result?.notFound) && result.notFound.length) details.push(`${result.notFound.length} SKU${result.notFound.length === 1 ? '' : 's'} not found in Finale: ${result.notFound.join(', ')}`)
	return details.length ? `${details.join('. ')}.` : 'SKU data refreshed successfully.'
}

function CatalogRefreshContent({ stores, selectedItemIds, close }) {
	const [selectedStores, setSelectedStores] = useState([])
	const [isRefreshing, setIsRefreshing] = useState(false)
	const [results, setResults] = useState([])
	const [progress, setProgress] = useState({ completed: 0, total: 0 })
	const [selectedSkuList, setSelectedSkuList] = useState([])
	const [isLoadingSelectedSkus, setIsLoadingSelectedSkus] = useState(selectedItemIds.length > 0)
	const [skuSource, setSkuSource] = useState(selectedItemIds.length > 0 ? 'selection' : 'manual')
	const [manualSkuText, setManualSkuText] = useState('')
	const mounted = useRef(true)
	const selectedItemIdsKey = selectedItemIds.join(',')

	useEffect(() => () => { mounted.current = false }, [])
	useEffect(() => {
		if (selectedItemIds.length === 0) {
			setSelectedSkuList([])
			setIsLoadingSelectedSkus(false)
			return
		}
		setIsLoadingSelectedSkus(true)
		fetchItems('eio', { idProductListJson: JSON.stringify(selectedItemIds) }, (response) => {
			if (!mounted.current) return
			setSelectedSkuList(normalizeSkuList(response?.data?.map((item) => item.sku)))
			setIsLoadingSelectedSkus(false)
		})
	}, [selectedItemIdsKey])

	const options = useMemo(() => stores.map((store) => ({
		value: store.id,
		label: store.name || `Store ${store.id}`,
		store
	})), [stores])
	const manualSkuList = useMemo(() => normalizeSkuList(manualSkuText.split(',')), [manualSkuText])
	const skuList = skuSource === 'selection' ? selectedSkuList : manualSkuList

	const submit = async (event) => {
		event.preventDefault()
		if (selectedStores.length === 0 || skuList.length === 0 || isRefreshing) return

		setIsRefreshing(true)
		setResults([])
		setProgress({ completed: 0, total: selectedStores.length })

		const selectedByApi = selectedStores.reduce((groups, option) => {
			const api = apiForStore(option.store.id, option.store.id_marketplace)
			if (!api) groups.unsupported = (groups.unsupported || []).concat(option)
			else groups[api] = (groups[api] || []).concat(option)
			return groups
		}, {})
		const jobs = []
		Object.entries(selectedByApi).forEach(([api, group]) => {
			if (api === 'unsupported') group.forEach((option) => jobs.push({ option, api: null }))
			else if (api === 'shopify') jobs.push({ api, options: group, shop: group.map(({ store }) => store.id).join(',') })
			else group.forEach((option) => jobs.push({ api, options: [option], shop: option.store.id }))
		})

		let completed = 0
		for (const job of jobs) {
			const response = job.api ? await requestRefresh({
				api: job.api,
				trigger: 'refresh_catalog',
				...(job.api === 'finale' ? {} : { shop: job.shop }),
				skuList: JSON.stringify(skuList)
			}) : { success: false, error: 'This store is not connected to a catalog refresh API.' }
			const error = errorText(response)
			const details = error ? null : successText(response)
			const resultRows = job.options ? job.options.map(({ store }) => ({
				id: store.id,
				name: store.name || `Store ${store.id}`,
				success: response?.success !== false && !error,
				error,
				details
			})) : [{ id: job.option.store.id, name: job.option.store.name || `Store ${job.option.store.id}`, success: false, error, details }]
			completed += resultRows.length
			if (mounted.current) {
				setResults((current) => current.concat(resultRows))
				setProgress({ completed, total: selectedStores.length })
			}
		}
		if (mounted.current) setIsRefreshing(false)
	}

	return <form onSubmit={submit}>
		<div className="form-group mb-3">
			<label className="font-weight-bold d-block">SKUs to refresh</label>
			{selectedItemIds.length > 0 && <div className="custom-control custom-radio mb-2">
				<input className="custom-control-input" type="radio" id="catalog-refresh-selection" checked={skuSource === 'selection'} disabled={isRefreshing} onChange={() => setSkuSource('selection')} />
				<label className="custom-control-label" htmlFor="catalog-refresh-selection">Use the {selectedItemIds.length} selected catalog item{selectedItemIds.length === 1 ? '' : 's'}{isLoadingSelectedSkus ? ' (loading SKUs…)' : ` (${selectedSkuList.length} SKU${selectedSkuList.length === 1 ? '' : 's'})`}</label>
			</div>}
			<div className="custom-control custom-radio mb-2">
				<input className="custom-control-input" type="radio" id="catalog-refresh-manual" checked={skuSource === 'manual'} disabled={isRefreshing} onChange={() => setSkuSource('manual')} />
				<label className="custom-control-label" htmlFor="catalog-refresh-manual">Enter SKU list manually</label>
			</div>
			{skuSource === 'manual' && <textarea className="form-control" rows="3" disabled={isRefreshing} value={manualSkuText} onChange={(event) => setManualSkuText(event.target.value)} placeholder="SKU-001, SKU-002, SKU with spaces" />}
			<div className="small text-muted mt-2">{skuSource === 'manual' ? `${manualSkuList.length} SKU${manualSkuList.length === 1 ? '' : 's'} entered. Separate SKUs with commas.` : 'Only the selected SKUs are saved back to the local catalog.'}</div>
		</div>
		{skuSource === 'selection' && !isLoadingSelectedSkus && skuList.length === 0 && <div className="alert alert-warning">The selected items do not contain usable SKUs. Enter a comma-separated list instead.</div>}
		<div className="form-group">
			<label className="font-weight-bold">Stores to refresh</label>
			<Select styles={refConst.styleList} isMulti isSearchable isDisabled={isRefreshing} options={options} value={selectedStores} onChange={(value) => setSelectedStores(value || [])} placeholder="Choose one or more stores" />
		</div>
		<div className="d-flex justify-content-between align-items-center mb-3">
			<span className="text-muted small">{selectedStores.length} store{selectedStores.length === 1 ? '' : 's'} selected</span>
			<div><button type="button" className="btn btn-light mr-2" disabled={isRefreshing} onClick={() => setSelectedStores(options)}>Select all</button><button type="button" className="btn btn-outline-secondary" disabled={isRefreshing} onClick={() => setSelectedStores([])}>Clear</button></div>
		</div>
		{isRefreshing && <div className="mb-3"><div className="d-flex justify-content-between small mb-1"><span>Refreshing catalog data…</span><span>{progress.completed}/{progress.total}</span></div><div className="progress"><div className="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" style={{ width: `${progress.total ? (progress.completed / progress.total) * 100 : 0}%` }} /></div></div>}
		{results.length > 0 && <div className="border rounded mb-3 overflow-auto" style={{ maxHeight: '260px' }}><table className="table table-sm mb-0"><thead className="thead-light"><tr><th>Store</th><th>Status</th><th>Details</th></tr></thead><tbody>{results.map((result) => <tr key={`${result.id}-${result.name}`}><td>{result.name}</td><td><span className={`badge badge-${result.success ? 'success' : 'danger'}`}>{result.success ? 'Refreshed' : 'Failed'}</span></td><td className="small text-break">{result.error || result.details}</td></tr>)}</tbody></table></div>}
		<div className="d-flex justify-content-end"><button type="button" className="btn btn-secondary mr-2" disabled={isRefreshing} onClick={close}>Close</button><button type="submit" className="btn btn-primary" disabled={isRefreshing || selectedStores.length === 0 || skuList.length === 0}>{isRefreshing ? 'Refreshing…' : `Refresh ${selectedStores.length || ''} store${selectedStores.length === 1 ? '' : 's'}`}</button></div>
	</form>
}

export default function CatalogRefreshModal(props) {
	const stores = useMemo(() => {
		const byId = new Map((props.stores || []).filter((store) => store?.id).map((store) => [String(store.id), store]))
		if (!byId.has(String(refConst.id_store_finale))) byId.set(String(refConst.id_store_finale), { id: refConst.id_store_finale, name: 'Finale' })
		return Array.from(byId.values()).sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
	}, [props.stores])
	// Catalog.selectItems mutates its selected-ID array before updating state, so
	// this must be read fresh on each render rather than memoized by array identity.
	const selectedItemIds = (props.itemsSelected || []).filter(Boolean)

	const open = () => props.modal({
		show: true,
		title: 'Refresh catalog data',
		html: () => <CatalogRefreshContent stores={stores} selectedItemIds={selectedItemIds} close={() => props.modal({ show: false })} />,
		exit: (_, closeModal) => closeModal(),
		ok: false
	})

	return <button type="button" className={props.className || 'btn btn-outline-success'} onClick={open}>Refresh catalog data</button>
}
